import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Table } from '../components/ui/Table';
import { PromoTabs } from '../components/banners/PromoTabs';
import { useAuth } from '../context/AuthContext';
import { listBannerCampaigns, type BannerCampaign, type BannerCampaignStatus } from '../services/resortAdminApi';

const STATUS_BADGE: Record<BannerCampaignStatus, string> = {
  Published: 'bg-green-100 text-green-800',
  Draft: 'bg-amber-100 text-amber-800',
  Expired: 'bg-gray-100 text-gray-600',
};

function formatDate(d: string | null): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const Banners: React.FC = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();

  const [campaigns, setCampaigns] = useState<BannerCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listBannerCampaigns(search || undefined, statusFilter || undefined, page, pageSize);
      setCampaigns(res.data || []);
      setTotal(res.pagination?.total ?? (res.data?.length || 0));
    } catch (e: any) {
      setError(e?.message || 'Failed to load banner campaigns');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = useCallback((v: string) => { setPage(1); setSearch(v); }, []);
  const handleFilter = useCallback((f: Record<string, string[]>) => {
    setPage(1);
    setStatusFilter((f.status || []).join(','));
  }, []);

  const columns = [
    {
      key: 'banners',
      header: 'Images',
      render: (_: any, row: BannerCampaign) => {
        const banners = row.banners || [];
        const shown = banners.slice(0, 2);
        const extra = banners.length - shown.length;
        return (
          <div className="flex -space-x-1">
            {shown.map((b, i) =>
              b.image_url ? (
                <img
                  key={i}
                  src={b.image_url}
                  alt=""
                  className="h-10 w-10 rounded-md border border-white object-cover"
                />
              ) : (
                <div key={i} className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-50">
                  <ImageIcon className="h-4 w-4 text-gray-300" />
                </div>
              )
            )}
            {extra > 0 && (
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-white bg-gray-100 text-xs font-medium text-gray-600">
                +{extra}
              </div>
            )}
            {banners.length === 0 && (
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-50">
                <ImageIcon className="h-4 w-4 text-gray-300" />
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'resort_name',
      header: 'Resort',
      render: (v: string) => <span className="font-medium text-gray-900">{v || '-'}</span>,
    },
    {
      key: 'content',
      header: 'Content',
      render: (_: any, row: BannerCampaign) => {
        const first = row.banners?.[0];
        if (!first?.title && !first?.description) return <span>-</span>;
        return (
          <div>
            <div className="text-gray-900">{first?.title || '-'}</div>
            {first?.description && <div className="text-xs text-gray-400">{first.description}</div>}
          </div>
        );
      },
    },
    {
      key: 'button',
      header: 'Button',
      render: (_: any, row: BannerCampaign) => {
        const first = row.banners?.[0];
        if (!first?.button_text && !first?.link) return <span>-</span>;
        return (
          <div className="max-w-[180px]">
            <div className="text-gray-900">{first?.button_text || '-'}</div>
            {first?.link && <div className="truncate text-xs text-gray-400">{first.link}</div>}
          </div>
        );
      },
    },
    {
      key: 'dates',
      header: 'Active dates',
      render: (_: any, row: BannerCampaign) => (
        <span className="whitespace-nowrap text-gray-900">
          {formatDate(row.start_date)} – {formatDate(row.end_date)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (v: BannerCampaignStatus) => (
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE[v] || 'bg-gray-100 text-gray-600'}`}>
          {v}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (_: any, row: BannerCampaign) => (
        <button
          onClick={() => navigate(`/banners/${encodeURIComponent(row.name)}`)}
          className="inline-flex items-center text-sm font-medium text-alps-primary hover:text-alps-primary-dark"
        >
          View <ArrowRight className="ml-1 h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PromoTabs />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Banners</h1>
          <p className="mt-1 text-sm text-gray-600">Promotional banner campaigns shown at the top of the app feed</p>
        </div>
        <button
          onClick={() => navigate('/banners/new')}
          disabled={isReadOnly}
          className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create
        </button>
      </div>

      {error && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <SearchFilter
        placeholder="Search by resort or content…"
        onSearch={handleSearch}
        onFilter={handleFilter}
        filterGroups={[{
          id: 'status',
          label: 'Status',
          options: [
            { id: 'Published', label: 'Published' },
            { id: 'Draft', label: 'Draft' },
            { id: 'Expired', label: 'Expired' },
          ],
        }]}
      />

      <Table
        columns={columns}
        data={campaigns}
        isLoading={loading}
        emptyMessage="No banner campaigns yet. Click Create to add one."
        pagination={{ page, page_size: pageSize, total }}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />
    </div>
  );
};
